import { type ReactNode, useCallback } from 'react';

import { ApiError } from '@/api/client/problem';
import { DeniedState, EmptyState, ErrorState, LoadingSkeleton } from '@/shared/feedback';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';

/**
 * The props every dashboard widget takes.
 *
 * One shape for all of them, so the page can lay them out without knowing what each one holds.
 */
export type DashboardWidgetProps = {
  readonly className?: string;
};

export type DashboardWidgetFrameProps<T> = {
  /** The widget's heading. */
  readonly title: string;
  /** The read. Must be stable — the loader is a dependency of the fetch effect. */
  readonly load: () => Promise<T>;
  /** What to render when the read succeeded. */
  readonly children: (data: T) => ReactNode;
  /** Whether a successful read is EMPTY. Absent means "never empty". */
  readonly isEmpty?: (data: T) => boolean;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly className?: string;
};

/**
 * The frame every dashboard widget renders inside.
 *
 * ---------------------------------------------------------------------------
 * WHY ONE FRAME
 * ---------------------------------------------------------------------------
 * A dashboard is many small reads on one page, and each of them can fail on its own. Written
 * inline, that is the same four-way switch (loading / empty / error / ready) copied seven
 * times — and the copy that swallowed an error and rendered "nothing here" is exactly the bug
 * this frame exists to prevent.
 *
 * So the frame owns the switch ONCE, and a widget supplies only its read and its content. The
 * page cannot be taken down by one failed read: a widget that fails renders its own error and
 * the rest of the dashboard stands.
 *
 * ---------------------------------------------------------------------------
 * THE FOUR STATES ARE DIFFERENT, AND THE READER MUST BE ABLE TO TELL THEM APART
 * ---------------------------------------------------------------------------
 *   - LOADING — a skeleton that promises the shape of what is coming.
 *   - EMPTY — the read succeeded and matched nothing. Nothing is wrong.
 *   - REFUSED — the server said no. The shared `DeniedState`, which describes the DESTINATION
 *     and never the requirement: no permission code, no "you would need X".
 *   - FAILED — something went wrong. The shared `ErrorState`, with a retry.
 *
 * A 403 renders the REFUSED state, not the FAILED one. They look different because they mean
 * different things, and retrying a refusal is pointless.
 */
export function DashboardWidgetFrame<T>({
  title,
  load,
  children,
  isEmpty,
  emptyTitle,
  emptyDescription,
  className,
}: DashboardWidgetFrameProps<T>): ReactNode {
  const { data, error, isLoading, reload } = useAsyncResource<T>(load);

  const retry = useCallback(() => reload(), [reload]);

  const body = ((): ReactNode => {
    if (isLoading) {
      return <LoadingSkeleton shape="card" label={`Loading ${title}`} />;
    }

    if (error !== null) {
      if (error instanceof ApiError && error.status === 403) {
        return (
          <DeniedState
            title="Not available"
            message="You do not have access to this information."
          />
        );
      }
      return <ErrorState message={error.message} onRetry={retry} />;
    }

    if (data === null) {
      return <ErrorState message="The dashboard did not receive a result." onRetry={retry} />;
    }

    if (isEmpty?.(data) === true) {
      return (
        <EmptyState
          title={emptyTitle ?? 'Nothing here yet'}
          description={emptyDescription}
        />
      );
    }

    return children(data);
  })();

  return (
    <section
      aria-label={title}
      className={[
        'flex flex-col gap-3 rounded-surface border border-border bg-surface-raised p-4',
        className ?? '',
      ].join(' ')}
    >
      <h2 className="text-sm font-semibold text-content">{title}</h2>
      {body}
    </section>
  );
}
